import Handle = __esri.Handle;
import {LayerEffectConfiguration} from "../config/layer-effect-configuration";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import {makeObservable, observable, reaction} from "mobx";
import {RisikoanalyseStore} from "../stores/risikoanalyse-store-mobx";


export interface LayerEffectProps {
    store: RisikoanalyseStore;
}

/**
 * Generic class for layer effects
 */
export abstract class LayerEffect {
    store: RisikoanalyseStore;
    view: __esri.MapView;
    layers: __esri.FeatureLayer[] = [];
    layerViews: __esri.FeatureLayerView[] = [];
    highlight: Handle | null = null;
    configs: LayerEffectConfiguration[] | null | undefined = [];
    observedConfig: LayerEffectConfiguration[] | null | undefined;

    // ready is truthy when all layer views and all layers are ready and loaded from the view
    ready: boolean = false;

    abstract filterType: string;

    protected constructor(props: LayerEffectProps) {
        this.store = props.store;
        this.observedConfig = props.store.mapViewerConfig.layerEffects;
        makeObservable(this, {
            observedConfig: observable
        });
        reaction(
            () => [this.store.mapStore.view, this.observedConfig],
            () => {
                this.view = this.store.mapStore.view as __esri.MapView;
                this.init();
            }
        );
    }

    /**
     * Initialize the layer effect.
     * Get layers and layer views specified in config from map view
     */
    init(): void {
        if (!this.store) {
            return;
        }

        this.configs = this.store.mapViewerConfig.getLayerEffectConfigs(this.filterType);
        if (!this.configs) {
            // no filter configuration found for this map filter type. do nothing
            return;
        }

        const layers: __esri.Layer[] = [];
        this.configs.forEach((config: LayerEffectConfiguration) => {
            const foundLayers = this.store.mapStore.getLayersByMetauid(config.serviceId, config.layerIds);
            layers.push(...foundLayers);
        });

        if (!layers || layers.length === 0) {
            // eslint-disable-next-line no-console
            console.debug(`No service layers found for map filter ${this.filterType}.`);
            return;
        }

        const layerViewPromises = layers.map((layer: FeatureLayer) => {
            layer.outFields = ["*"];
            return this.view.whenLayerView(layer);
        });

        Promise.all(layerViewPromises).then((layerViews) => {
            layerViews.forEach((lw) => {
                if (!this.layers.includes(lw.layer)) {
                    this.layers.push(lw.layer);
                }
                if (!this.layerViews.includes(lw)) {
                    this.layerViews.push(lw);
                }
            });
            this.ready = true;
        }).catch((e) => {
            // eslint-disable-next-line no-console
            console.debug(`Error waiting for layer views in map filter ${this.filterType}.`, e);
        });
    }

    /**
     * Apply the layer effect.
     */
    abstract apply(): void;
}
